/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { html } from 'lit';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './link-list-section.scss';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import C4DContentSection from '../content-section/content-section';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Link list.
 *
 * @element c4d-link-list
 * @slot heading - The heading content.
 * @csspart grid - The outer wrapper. Usage `c4d-link-list-section::part(grid)`
 * @csspart row - The inner wrapper. Usage `c4d-link-list-section::part(row)`
 * @csspart heading - The heading's column container. Usage `c4d-link-list-section::part(heading)`
 * @csspart children - The link-list's column container. Usage `c4d-link-list-section::part(children)`
 */
@customElement(`${c4dPrefix}-link-list-section`)
class C4DLinkListSection extends StableSelectorMixin(C4DContentSection) {
  _handleSlotChange = (event: Event) => {
    const slot = (event.target as HTMLSlotElement)
      .assignedElements()
      .filter(
        (elem) =>
          elem.localName ===
          (this.constructor as typeof C4DLinkListSection).linkListSelector
      )[0];
    if (
      slot &&
      (!slot.hasAttribute('type') || slot.getAttribute('type') !== 'end')
    ) {
      slot.setAttribute('type', 'end');
    }
  };

  render() {
    const { _handleSlotChange: handleSlotChange } = this;
    return html`
      <div class="${prefix}--content-section__grid" part="grid">
        <div class="${prefix}--content-section__row" part="row">
          <div class="${prefix}--content-section__left" part="heading">
            <slot name="heading"></slot>
          </div>
          <div class="${prefix}--content-section__children" part="children">
            <slot @slotchange="${handleSlotChange}"></slot>
          </div>
        </div>
      </div>
    `;
  }

  static get linkListSelector() {
    return `${c4dPrefix}-link-list`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader

  static get stableSelector() {
    return `${c4dPrefix}--link-list-section`;
  }
}

console.warn(
  'The link-list-section component has been deprecated in favor of the content-section/block and link-list end of section variant components. ' +
    'See content-section/block and link-list documentation for more information.'
);

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DLinkListSection;
